import { Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon } from '@persona-id/design-tokens/icons';
import { MouseEvent, useState } from 'react';
import styled from 'styled-components';

import Banner from 'design-system/components/Banner';
import { paragraphMixins } from 'design-system/mixins/text';
import { Elevation } from 'design-system/variables/effect';
import { LocalStorageKey } from 'lib/clients/StorageClient';
import useLocalStorage from 'lib/hooks/useLocalStorage';
import { focusTitleElement } from 'verify/lib/a11y';
import { Z_INDICES } from 'verify/styles/variables';

const StyledBanner = styled(Banner)`
  ${paragraphMixins.md};
  position: absolute;
  top: ${Spacing[2]};
  left: ${Spacing[2]};
  right: ${Spacing[2]};
  z-index: ${Z_INDICES.notifications};
  box-shadow: ${Elevation.Low};
`;

const IconContainer = styled('div')`
  height: 40px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MessageContainer = styled('div')`
  margin: ${Spacing[2]};
`;

// Usees h1 for a11y / focus purposes.
const Title = styled.h1.attrs(() => ({ tabIndex: -1 }))`
  ${paragraphMixins.md};
  color: ${({ theme }) => theme.color.text.primary};
`;

const DOCS_LINK = 'https://help.withpersona.com/articles/11tePoksKZ5kSCo7pdC800/';

export default function SandboxStyledBanner() {
  const [isDismissed, setIsDismissed] = useState(false);
  const [isPermanentlyDismissed, setPermanentlyDismissed] = useLocalStorage<boolean>(
    LocalStorageKey.InquirySandboxBannerHidden,
    false,
  );

  const handleClickDismiss = () => {
    setIsDismissed(true);
    // Return focus to current page heading
    // Without timeout, will focus on close button instead
    setTimeout(focusTitleElement, 0);
  };

  const handleClickDontShowAgain = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setPermanentlyDismissed(true);
    // Return focus to current page heading
    // Without timeout, will focus on close button instead
    setTimeout(focusTitleElement, 0);
  };

  return isDismissed || isPermanentlyDismissed ? null : (
    <StyledBanner data-test='sandbox-banner' variant='alert' onClose={handleClickDismiss}>
      <IconContainer>
        <CircleAlertIcon size={24} />
      </IconContainer>
      <MessageContainer>
        <Title>
          <span>
            You are in a Sandbox environment. No verifications or extractions are performed and you
            will see simulated data.
          </span>{' '}
          <a href={DOCS_LINK} target='_blank' rel='noreferrer'>
            Learn more.
          </a>
        </Title>
        <div>
          <a href='#' id='sandbox-banner__dismiss-forever' onClick={handleClickDontShowAgain}>
            Don‘t show me again.
          </a>
        </div>
      </MessageContainer>
    </StyledBanner>
  );
}
